import {AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Profile} from '../../../shared/interface/profile';
import {AuthService} from '../../../shared/services/auth.service';
import {first, takeUntil} from 'rxjs/operators';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AvatarCropDialogComponent} from '../avatar-crop-dialog/avatar-crop-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {ReplaySubject} from 'rxjs';
import {Uuid} from '../../../shared/interface/response/uuid';
import {UserService} from '../../../shared/services/user.service';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {validPhoneNumber} from '../../../shared/validator/phone-validator';
import {validEmailAddress} from '../../../shared/validator/email-validator';
import {UserPermissions} from "../../../shared/interface/user-permissions";

@Component({
  selector: 'app-profile-dialog',
  templateUrl: './profile-dialog.component.html',
  styleUrls: ['./profile-dialog.component.scss']
})
export class ProfileDialogComponent implements AfterViewInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  profileForm: FormGroup;
  processingRequest = false;
  profileObject: Profile;
  imgCnt = 0;
  avatarSelectControl: FormControl;
  @ViewChild('avatarSelect') avatarSelect: ElementRef;
  formErrors: any = {};
  userPermissions: ReplaySubject<UserPermissions>;

  @Output() avatarChanged: EventEmitter<number> = new EventEmitter();

  constructor(
    private auth: AuthService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ProfileDialogComponent>,
    private dialog: MatDialog,
    private userService: UserService,
    private formHelper: FormHelperService,
    private snack: MatSnackBar,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.avatarSelectControl = fb.control('');
    this.profileForm = fb.group({
      firstName: fb.control('', [Validators.required]),
      lastName: fb.control('', [Validators.required]),
      phoneNumber: fb.control('', [Validators.required, validPhoneNumber()]),
      email: fb.control('', [Validators.required, validEmailAddress()]),
      position: fb.control(''),
      mailingEmail: fb.control(''),
      mailingH: fb.control(''),
    });

    this.auth.getProfile()
      .pipe(first())
      .subscribe((profile: Profile) => {
        this.profileObject = profile;
        this.profileForm.reset({
          firstName: profile.firstName,
          lastName: profile.lastName,
          phoneNumber: profile.phone,
          email: profile.email,
          position: profile.position,
          mailingEmail: profile.mailingEmail,
        });
      });
  }

  ngAfterViewInit(): void {
    this.avatarSelectControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val: any) => {
        if (!this.avatarSelect.nativeElement.files[0]) {
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(this.avatarSelect.nativeElement.files[0]);
        reader.onload = () => {
          const fileBase64 = reader.result.toString();
          const dialogRef = this.dialog.open(AvatarCropDialogComponent, {
            width: '55%',
            data: {fileBase64, ratio: 1, roundCropper: true, maintainRatio: true, compressedFile: false},
            height: '610px',
            maxHeight: '90%',
            panelClass: 'full-height-dialog',
          });

          dialogRef.afterClosed().subscribe(result => {
            if (!result) {
              this.avatarSelectControl.reset();
              return;
            }

            this.userService.updateProfileAvatar(result)
              .subscribe(() => {
                this.snack.open('Pomyślnie zmieniono zdjęcie', '', {duration: 2000});
                this.imgCnt++;
                this.avatarChanged.emit(this.imgCnt);
                this.avatarSelectControl.reset();
              }, (error: HttpErrorResponse) => {
                this.snack.open('Przy aktualizacji zdjęcia wystąpił błąd', error.status.toString(), {duration: 2000});
                this.avatarSelectControl.reset();
              });
          });
        };
      });
  }

  public openFileDialog(): void {
    const e: HTMLElement = this.avatarSelect.nativeElement;
    e.click();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  save(): void {
    if (!this.profileForm.valid) {
      this.profileForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }

    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.profileForm, this.formErrors);
    this.formErrors = {};

    this.userService.updateProfile(this.profileForm.getRawValue())
      .pipe(
        first()
      )
      .subscribe((response: Uuid) => {
        this.snack.open('Pomyślnie zaktualizowano profil', '', {
          duration: 2000,
        });
        this.processingRequest = false;

        this.auth.loadProfile();
        this.dialogRef.close();
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;
        this.snack.open('Podczas aktualizacji profilu wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.profileForm, this.formErrors);
        }
      })
    ;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  setAvatar(): void {
    this.openFileDialog();
  }
}
